import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Erro } from '../../componentes/estados';
import { Alternador, Entrada, Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { EscolherArquivo } from './Arquivos';
import { EVENTOS_MODELO, REGIMES, TIPOS_MODELO, invalidarDocumentos, type CampoMapa, type ChaveModelo, type Modelo } from './tipos';

/** Sobe o .docx pelo `/anexos` do núcleo para o modelo `modeloId` (ainda pendente, no novo) e devolve o id do anexo. */
async function subirDocx(modeloId: string, arquivo: File) {
  const fd = new FormData();
  fd.append('entidade', 'documentos.modelo');
  fd.append('registro_id', modeloId);
  fd.append('arquivo', arquivo);
  return (await api<{ id: string }>('POST', '/anexos', fd)).id;
}

/**
 * D24: modelo de contrato em .docx (o arquivo do Word com campos de formulário). Novo: nome, filtros da escolha automática
 * e o arquivo; o sistema lê os campos e sugere o dado de cada um. Existente: os mesmos filtros, a troca do arquivo e o mapa
 * campo a campo (o dado, se é obrigatório e, no "outro", o rótulo que aparece no envio). Mudar o arquivo ou o mapa sobe a versão.
 */
export function FormModeloDocx({ modelo, aoFechar, aoCriar }: { modelo: Modelo | null; aoFechar: () => void; aoCriar: (m: Modelo) => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => modelo?.id ?? crypto.randomUUID(), [modelo]);
  const podeEditar = s.pode('documentos.configurar');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const enviado = useRef<string | null>(null);
  const [remover, setRemover] = useState(false);
  const [mapa, setMapa] = useState<CampoMapa[]>(modelo?.campos ?? []);
  const chaves = useQuery({ queryKey: ['documentos', 'chaves'], queryFn: () => get<{ itens: ChaveModelo[] }>('/documentos/modelos/chaves') });
  const empresas = s.empresas?.opcoes ?? [];
  const excluir = useMutation({
    mutationFn: () => del(`/documentos/modelos/${id}`),
    onSuccess: async () => {
      await invalidarDocumentos(qc);
      aoFechar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, desabilitado: !podeEditar },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', desabilitado: !podeEditar },
    { nome: 'tipo', rotulo: t('docx.tipo'), tipo: 'selecao', largura: 'meia', desabilitado: !podeEditar, opcoes: TIPOS_MODELO.map((v) => ({ valor: v, rotulo: t(`docx.tipos.${v}`) })) },
    {
      nome: 'evento',
      rotulo: t('docx.evento'),
      tipo: 'selecao',
      largura: 'meia',
      desabilitado: !podeEditar,
      visivel: (v) => v.tipo !== 'contrato',
      opcoes: [{ valor: '', rotulo: t('docx.nenhum') }, ...EVENTOS_MODELO.map((v) => ({ valor: v, rotulo: t(`docx.eventos.${v}`) }))],
    },
    { nome: 'so_contratos_antigos', rotulo: t('docx.soAntigos'), tipo: 'booleano', desabilitado: !podeEditar, ajuda: t('docx.soAntigosAjuda'), visivel: (v) => v.tipo !== 'contrato' },
    {
      nome: 'para_empresa_id',
      rotulo: t('docx.empresa'),
      tipo: 'selecao',
      largura: 'meia',
      desabilitado: !podeEditar,
      ajuda: t('docx.empresaAjuda'),
      opcoes: [{ valor: '', rotulo: t('docx.todas') }, ...empresas.map((e) => ({ valor: e.id, rotulo: e.nome }))],
    },
    { nome: 'regime', rotulo: t('docx.regime'), tipo: 'selecao', largura: 'meia', desabilitado: !podeEditar, opcoes: [{ valor: '', rotulo: t('docx.todos') }, ...REGIMES.map((v) => ({ valor: v, rotulo: t(`docx.regimes.${v}`) }))] },
    {
      nome: 'servico_id',
      rotulo: t('docx.servico'),
      tipo: 'busca',
      desabilitado: !podeEditar,
      ajuda: t('docx.servicoAjuda'),
      busca: { caminho: '/cadastros/servicos', rotulo: (i) => String(i.nome ?? '') },
    },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', desabilitado: !podeEditar },
  ];
  const inicial = modelo
    ? { nome: modelo.nome, descricao: modelo.descricao ?? '', tipo: modelo.tipo, evento: modelo.evento ?? '', para_empresa_id: modelo.para_empresa_id ?? '', regime: modelo.regimes[0] ?? '', servico_id: modelo.servicos[0] ?? null, so_contratos_antigos: modelo.so_contratos_antigos, ativo: modelo.ativo }
    : { tipo: 'contrato', evento: '', para_empresa_id: '', regime: '', so_contratos_antigos: false, ativo: true };
  const opcoesChave = (chaves.data?.itens ?? []).map((c) => ({ valor: c.chave, rotulo: c.origem === 'envio' ? `${c.rotulo} (${t('docx.noEnvio')})` : c.rotulo }));
  const rotuloDaChave = (chave: string) => chaves.data?.itens.find((c) => c.chave === chave)?.rotulo ?? chave;
  const mudar = (i: number, m: Partial<CampoMapa>) => setMapa((atual) => atual.map((c) => (c.indice === i ? { ...c, ...m } : c)));

  return (
    <>
      <Drawer
        aberto={!remover}
        aoFechar={aoFechar}
        largura="lg"
        titulo={modelo ? textoLivre(modelo.nome) : t('docx.novo')}
        rodape={
          podeEditar ? (
            <>
              {modelo && (
                <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(true)}>
                  {t('acoes.excluir')}
                </Botao>
              )}
              <Botao variante="primario" type="submit" form="form-modelo-docx">
                {t('acoes.salvar')}
              </Botao>
            </>
          ) : (
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
          )
        }
      >
        <p className="mb-3 text-sm text-tinta-3">{t('docx.ajuda')}</p>
        <Formulario
          id="form-modelo-docx"
          campos={campos}
          inicial={inicial}
          aoEnviar={async (v) => {
            const filtros = {
              tipo: v.tipo,
              evento: v.tipo === 'contrato' ? null : String(v.evento ?? '') || null,
              para_empresa_id: String(v.para_empresa_id ?? '') || null,
              regimes: v.regime ? [String(v.regime)] : [],
              servicos: v.servico_id ? [String(v.servico_id)] : [],
              so_contratos_antigos: v.tipo !== 'contrato' && !!v.so_contratos_antigos,
            };
            const corpo = { nome: v.nome, descricao: String(v.descricao ?? '').trim() || null, ativo: !!v.ativo, ...filtros };
            if (!modelo) {
              if (!arquivo) {
                toast.erro(t('arquivoObrigatorio'));
                return;
              }
              enviado.current ??= await subirDocx(id, arquivo);
              await post('/documentos/modelos/docx', { id, anexo_id: enviado.current, ...corpo }, { 'Idempotency-Key': id });
              await invalidarDocumentos(qc);
              toast.sucesso(t('docx.importado'));
              aoCriar(await get<Modelo>(`/documentos/modelos/${id}`));
              return;
            }
            if (arquivo) {
              enviado.current ??= await subirDocx(id, arquivo);
              await post(`/documentos/modelos/${id}/arquivo`, { anexo_id: enviado.current });
            }
            await patch(`/documentos/modelos/${id}`, arquivo ? corpo : { ...corpo, campos: mapa });
            await invalidarDocumentos(qc);
            toast.sucesso(arquivo ? t('docx.arquivoTrocado') : tn('comum.salvo'));
            if (arquivo) aoCriar(await get<Modelo>(`/documentos/modelos/${id}`));
            else aoFechar();
          }}
        >
          {podeEditar && (
            <EscolherArquivo
              arquivo={arquivo}
              rotulo={modelo ? t('docx.trocarArquivo', { versao: modelo.versao }) : t('docx.arquivo')}
              aceitar=".docx"
              aoEscolher={(f) => {
                enviado.current = null;
                setArquivo(f);
              }}
            />
          )}
          {modelo && (
            <section className="flex min-w-0 flex-col gap-2 sm:col-span-6" aria-label={t('docx.mapa')}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-tinta-2">{t('docx.mapa')}</span>
                <Selo>{t('docx.versao', { versao: modelo.versao })}</Selo>
              </div>
              <p className="text-xs text-tinta-3">{arquivo ? t('docx.mapaDepois') : t('docx.mapaAjuda')}</p>
              {chaves.isError && <Erro erro={chaves.error} aoTentar={() => chaves.refetch()} />}
              <ol className="flex min-w-0 flex-col divide-y divide-borda rounded-md border border-borda">
                {mapa.map((c) => (
                  <li key={c.indice} className="grid min-w-0 gap-2 p-3 sm:grid-cols-12 sm:items-center">
                    <p className="min-w-0 text-xs break-words text-tinta-3 sm:col-span-5">
                      <span className="font-mono text-tinta-2">{c.indice + 1}.</span> {c.antes ? `…${c.antes}` : ''} <span className="rounded border border-borda px-1 font-mono text-tinta-1">[ ]</span> {c.depois ? `${c.depois}…` : ''}
                    </p>
                    <div className="min-w-0 sm:col-span-4">
                      <Selecao
                        aria-label={t('docx.dadoDoCampo', { n: c.indice + 1 })}
                        value={c.chave}
                        disabled={!podeEditar || !!arquivo}
                        opcoes={opcoesChave}
                        onChange={(e) => mudar(c.indice, { chave: e.target.value, rotulo: e.target.value === 'outro' ? c.rotulo : rotuloDaChave(e.target.value) })}
                      />
                    </div>
                    <div className="flex min-w-0 items-center gap-2 sm:col-span-3">
                      <Alternador rotulo={t('docx.obrigatorio')} marcado={c.obrigatorio} desabilitado={!podeEditar || !!arquivo} aoMudar={(x) => mudar(c.indice, { obrigatorio: x })} />
                      <span className="text-xs text-tinta-2">{t('docx.obrigatorio')}</span>
                    </div>
                    {c.chave === 'outro' && (
                      <div className="min-w-0 sm:col-span-12">
                        <Entrada aria-label={t('docx.rotuloDoCampo', { n: c.indice + 1 })} value={c.rotulo} maxLength={120} disabled={!podeEditar || !!arquivo} onChange={(e) => mudar(c.indice, { rotulo: e.target.value })} />
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </Formulario>
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluirModelo')} texto={t('confirmar.excluirModeloTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
